import MenuIcon from '@mui/icons-material/Menu';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import SpotifyService, { SpotifyUser } from '../services/spotifyService';
import JukoBoxLogo from './JukoBoxLogo';

const Navbar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { items } = useCart();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [spotifyUser, setSpotifyUser] = useState<SpotifyUser | null>(null);
  const open = Boolean(anchorEl);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const handleDashboard = () => {
    handleClose();
    setMobileMenuOpen(false);
    navigate('/dashboard');
  };

  const handleNavigation = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      const userInfo: SpotifyUser | null = await SpotifyService.getUserInfo();
      setSpotifyUser(userInfo);
    };
    !spotifyUser && fetchUserInfo();
  }, [user]);

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: 'background.paper',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
      }}
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          minHeight: { xs: '56px', sm: '64px' },
          px: { xs: 1.5, sm: 2, md: 3 },
        }}
      >
        <Box
          component={RouterLink}
          to="/"
          sx={{
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <JukoBoxLogo size={isMobile ? 40 : 50} />
        </Box>

        {isMobile ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              component={RouterLink}
              to="/cart"
              sx={{
                mr: 1,
                color: 'text.primary',
                width: { xs: 40, sm: 44 },
                height: { xs: 40, sm: 44 },
              }}
            >
              <Badge
                badgeContent={items.length}
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.7rem',
                    height: '18px',
                    minWidth: '18px',
                  },
                }}
              >
                <ShoppingCartIcon fontSize={isMobile ? 'small' : 'medium'} />
              </Badge>
            </IconButton>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleMobileMenuToggle}
              sx={{
                color: 'text.primary',
                width: { xs: 40, sm: 44 },
                height: { xs: 40, sm: 44 },
              }}
              aria-label="menu"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              color="inherit"
              component={RouterLink}
              to="/faq"
              sx={{
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              FAQ
            </Button>
            <IconButton
              color="inherit"
              component={RouterLink}
              to="/cart"
              sx={{
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <Badge badgeContent={items.length} color="primary">
                <ShoppingCartIcon />
              </Badge>
            </IconButton>
            {isAuthenticated && user ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      display: { xs: 'none', sm: 'block' },
                    }}
                  >
                    {spotifyUser?.display_name || user.firstName}
                  </Typography>
                  <Avatar
                    alt={spotifyUser?.display_name || user.firstName}
                    src={spotifyUser?.images?.[0]?.url}
                    sx={{
                      width: 32,
                      height: 32,
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: 'primary.main',
                    }}
                    onClick={handleMenu}
                  />
                </Box>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  PaperProps={{
                    sx: {
                      bgcolor: 'background.paper',
                      mt: 1.5,
                      minWidth: 180,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                      borderRadius: 2,
                    },
                  }}
                >
                  <MenuItem onClick={handleDashboard}>
                    <Typography variant="body2">Mon tableau de bord</Typography>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      navigate('/orders');
                    }}
                  >
                    <Typography variant="body2">Mes commandes</Typography>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <Typography variant="body2" color="error">
                      Déconnexion
                    </Typography>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                component={RouterLink}
                to="/login"
                sx={{
                  borderRadius: '50px',
                  px: 3,
                  py: 1,
                  boxShadow: '0 4px 14px 0 rgba(255, 107, 107, 0.2)',
                  '&:hover': {
                    boxShadow: '0 6px 20px 0 rgba(255, 107, 107, 0.3)',
                  },
                }}
              >
                Connexion
              </Button>
            )}
          </Box>
        )}
      </Toolbar>
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: '70%',
            maxWidth: '300px',
            backgroundColor: 'background.paper',
            p: 2,
            transition: (theme) =>
              theme.transitions.create(['transform'], {
                duration: theme.transitions.duration.standard,
              }),
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        }}
      >
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <JukoBoxLogo size={40} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <List>
          <ListItemButton
            onClick={() => handleNavigation('/store')}
            sx={{ borderRadius: 1, mb: 1 }}
          >
            <ListItemText
              primary="Boutique"
              sx={{ '& .MuiListItemText-primary': { fontSize: '1rem' } }}
            />
          </ListItemButton>
          <ListItemButton onClick={() => handleNavigation('/cart')} sx={{ borderRadius: 1, mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <ListItemText
                primary="Panier"
                sx={{ '& .MuiListItemText-primary': { fontSize: '1rem' } }}
              />
              {items.length > 0 && (
                <Badge badgeContent={items.length} color="primary" sx={{ ml: 1 }}>
                  <ShoppingCartIcon fontSize="small" />
                </Badge>
              )}
            </Box>
          </ListItemButton>
          <ListItemButton onClick={() => handleNavigation('/faq')} sx={{ borderRadius: 1, mb: 1 }}>
            <ListItemText
              primary="FAQ"
              sx={{ '& .MuiListItemText-primary': { fontSize: '1rem' } }}
            />
          </ListItemButton>

          {isAuthenticated && user ? (
            <>
              <ListItemButton onClick={handleDashboard} sx={{ borderRadius: 1, mb: 1 }}>
                <ListItemText
                  primary="Mon tableau de bord"
                  sx={{ '& .MuiListItemText-primary': { fontSize: '1rem' } }}
                />
              </ListItemButton>
              <ListItemButton
                onClick={() => {
                  handleMobileMenuToggle();
                  navigate('/orders');
                }}
                sx={{ borderRadius: 1, mb: 1 }}
              >
                <ListItemText
                  primary="Mes commandes"
                  sx={{ '& .MuiListItemText-primary': { fontSize: '1rem' } }}
                />
              </ListItemButton>
              <Divider sx={{ my: 1.5 }} />
              <ListItemButton onClick={handleLogout} sx={{ borderRadius: 1 }}>
                <ListItemText
                  primary="Déconnexion"
                  sx={{
                    color: 'error.main',
                    '& .MuiListItemText-primary': { fontSize: '1rem' },
                  }}
                />
              </ListItemButton>
            </>
          ) : (
            <>
              <Divider sx={{ my: 1.5 }} />
              <ListItemButton
                onClick={() => handleNavigation('/login')}
                sx={{
                  borderRadius: 1,
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                }}
              >
                <ListItemText
                  primary="Connexion"
                  sx={{
                    color: 'white',
                    '& .MuiListItemText-primary': {
                      fontSize: '1rem',
                      fontWeight: 'bold',
                    },
                  }}
                />
              </ListItemButton>
            </>
          )}
        </List>
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
